/* Cart Drawer — AJAX cart interactions */

const CartDrawer = (() => {
  const drawer = document.getElementById('cart-drawer');
  const overlay = document.getElementById('cart-overlay');

  function open() {
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    drawer.querySelector('.cart-drawer__close').focus();
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    overlay.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  function setLoading(state) {
    drawer.classList.toggle('is-loading', state);
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function updateCartUI() {
    const cart = await fetchCart();
    renderCartItems(cart);
    updateCountBadge(cart.item_count);
  }

  function renderCartItems(cart) {
    const body = document.getElementById('cart-drawer-body');
    const footer = document.getElementById('cart-footer');
    const subtotal = document.querySelector('[data-cart-subtotal]');

    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty" id="cart-empty">
          <p>Your cart is empty.</p>
          <button class="btn btn--outline" data-cart-close>Continue shopping</button>
        </div>`;
      footer.style.display = 'none';
    } else {
      const items = cart.items.map((item, i) => buildItemHTML(item, i + 1)).join('');
      body.innerHTML = `<ul class="cart-items" id="cart-items">${items}</ul>`;
      footer.style.display = '';
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    }

    bindItemEvents();
  }

  function buildItemHTML(item, line) {
    const imageHTML = item.image
      ? `<img src="${item.image.replace(/\.\w+$/, m => '_80x80' + m)}" alt="${escapeHTML(item.product_title)}" width="80" height="80" loading="lazy" class="cart-item__image">`
      : `<div class="cart-item__image cart-item__image--placeholder"></div>`;

    const variantHTML = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="cart-item__variant">${escapeHTML(item.variant_title)}</p>`
      : '';

    return `
      <li class="cart-item" data-cart-item data-line="${line}">
        <a href="${item.url}" class="cart-item__image-link">${imageHTML}</a>
        <div class="cart-item__details">
          <a href="${item.url}" class="cart-item__name">${escapeHTML(item.product_title)}</a>
          ${variantHTML}
          <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
          <div class="cart-item__quantity">
            <button class="qty-btn" aria-label="Decrease quantity" data-change-qty="${line}" data-delta="-1">−</button>
            <span class="qty-value">${item.quantity}</span>
            <button class="qty-btn" aria-label="Increase quantity" data-change-qty="${line}" data-delta="1">+</button>
          </div>
        </div>
        <button class="cart-item__remove" aria-label="Remove ${escapeHTML(item.product_title)}" data-remove-item="${line}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </li>`;
  }

  function bindItemEvents() {
    document.querySelectorAll('[data-change-qty]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const line = parseInt(e.currentTarget.dataset.changeQty);
        const delta = parseInt(e.currentTarget.dataset.delta);
        const item = document.querySelector(`[data-line="${line}"]`);
        const currentQty = parseInt(item.querySelector('.qty-value').textContent);
        const newQty = Math.max(0, currentQty + delta);
        await changeLineItem(line, newQty);
      });
    });

    document.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const line = parseInt(e.currentTarget.dataset.removeItem);
        await changeLineItem(line, 0);
      });
    });

    document.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', close);
    });
  }

  async function changeLineItem(line, quantity) {
    setLoading(true);
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      await updateCartUI();
    } finally {
      setLoading(false);
    }
  }

  async function addToCart(variantId, quantity = 1) {
    setLoading(true);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) throw new Error('Add to cart failed');
      await updateCartUI();
      open();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  function updateCountBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  function escapeHTML(str) {
    return str.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  function init() {
    if (!drawer) return;

    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.addEventListener('click', open);
    });

    document.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', close);
    });

    overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });

    document.querySelectorAll('.add-to-cart').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const variantId = e.currentTarget.dataset.productId;
        if (!variantId) return;
        e.currentTarget.textContent = 'Adding…';
        e.currentTarget.disabled = true;
        await addToCart(variantId);
        e.currentTarget.textContent = 'Add to cart';
        e.currentTarget.disabled = false;
      });
    });

    bindItemEvents();
  }

  return { init, open, close, addToCart };
})();

document.addEventListener('DOMContentLoaded', () => CartDrawer.init());

/* Product Gallery — thumbnail switching, scoped per card */
(function () {
  document.querySelectorAll('.product-card').forEach(card => {
    const thumbs = card.querySelectorAll('[data-gallery-src]');
    const mainImg = card.querySelector('[data-gallery-main]');
    if (!thumbs.length || !mainImg) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        mainImg.style.opacity = '0';
        mainImg.onload = () => { mainImg.style.opacity = '1'; };
        mainImg.src = thumb.dataset.gallerySrc;
        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  });
})();
